import type { CategoryEntity } from '@ts/core/src/modules/category/category.entity'
import type { BrandEntity } from '@ts/core/src/modules/brand/brand.entity'
import { PaginatedQueryConfig, usePaginatedQuery } from 'react-query'

import { Paginated } from '../../types/pagination'
import { useBackendContext, BackendQueryOptions } from '../../components/BackendContext'
import { useAsyncDebounce } from '../../utils'
import CategoryGroupEntity from '@ts/core/src/modules/category/categorygroup.entity'
import type { ProductType } from '@ts/core/src/modules/coreproduct/coreproduct.types'

export interface UseProductsQueryProps {
  page?: number
  itemsPerPage?: number
  /** The slug of the store to fetch products for. */
  store?: string
  search?: string
  categories?: string[]
  brands?: string[]
  groups?: string[]
  ids?: string[]
  group?: string
  region?: string
  barcodes?: string
  special?: boolean
  everydayValue?: boolean
  totals?: boolean
  lazy?: boolean
  debounce?: number
  minPrice?: number
  maxPrice?: number
  sort?: string
  order?: 'ASC' | 'DESC'
  type?: ProductType
  bundleGroup?: string
  facetByCategory?: boolean
}

type SearchResponse<Product> = {
  categories: (CategoryEntity & { count: number; groupId: string })[]
  brands: (BrandEntity & { count: number })[]
  groups: (CategoryGroupEntity & { count: number })[]
  results: Paginated<Product>
}

export function useProductsQuery<Product>(
  options: UseProductsQueryProps = {},
  settings?: PaginatedQueryConfig<any, any>,
) {
  const {
    page = 1,
    itemsPerPage = 12,
    store,
    categories,
    brands,
    group,
    groups,
    search,
    region,
    special,
    everydayValue,
    totals,
    lazy,
    debounce,
    minPrice,
    maxPrice,
    ids,
    sort,
    order,
    barcodes,
    type,
    bundleGroup,
    facetByCategory,
  } = options

  const { query } = useBackendContext()
  const debouncedQuery = useAsyncDebounce(query, debounce)

  const _query = debounce ? debouncedQuery : query

  const url = 'products/search'

  const params = {
    page,
    limit: itemsPerPage,
    categories: categories ? categories.join(',') : undefined,
    brands: brands ? brands.join(',') : undefined,
    groups: groups ? groups.join(',') : undefined,
    ids: ids ? ids.join(',') : undefined,
    store,
    region,
    totals: totals ? true : undefined,
    group,
    special: special ? true : undefined,
    everydayValue: everydayValue ? true : undefined,
    minPrice,
    maxPrice,
    query: search,
    sort,
    order,
    barcodes,
    type,
    bundleGroup,
    facetByCategory: facetByCategory ? true : undefined,
  }

  const _settings = {
    refetchIntervalInBackground: false,
    refetchOnWindowFocus: false,
    ...settings,
  }

  const response = usePaginatedQuery<SearchResponse<Product>, [string, BackendQueryOptions<any>]>(
    [url, { params }],
    _query,
    _settings,
  )

  return response
}

export default useProductsQuery
